import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import RedditLogo from '@/assets/icons/reddit-logo.png';
import useAlerts from '@/hooks/useAlerts';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';
import {
  generateSingleImage,
  generateImagesInParallel,
  downloadImagesAsZip as downloadZip,
  downloadSelectedImagesAsZip,
} from '@/utils/coverImages/imageGeneration';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (variation: ImageVariation): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var32');

            const leftTextHolder = document.createElement('div');
            leftTextHolder.classList.add('left-text-holder-var32');

            const leftAuthorRow = document.createElement('div');
            leftAuthorRow.classList.add('author-row-var32');

            const leftAuthorImageEl = new Image();
            leftAuthorImageEl.classList.add('author-image-var32');

            const leftAuthorName = document.createElement('p');
            leftAuthorName.innerText = variation.leftAuthorName || '';
            leftAuthorName.classList.add('author-name-var32');

            if (variation.leftChannelNameColor === 'blue') {
              leftAuthorName.classList.add('author-name-var32__blue');
            }

            if (variation.leftChannelImageOption === 'reddit') {
              leftAuthorImageEl.src = RedditLogo;
              leftAuthorRow.appendChild(leftAuthorImageEl);
            } else {
              if (!!variation.leftAuthorImage) {
                leftAuthorImageEl.src = variation.leftAuthorImage;
                leftAuthorRow.appendChild(leftAuthorImageEl);
              }
            }

            leftAuthorRow.appendChild(leftAuthorName);

            const leftText = document.createElement('div');
            leftText.classList.add('right-text-var32'); // reuse same text class styles

            const leftTextP = document.createElement('p');

            let leftTextValue = replaceQuotes(variation.leftText || '');

            const leftAlg = getOptimalFontSize(leftTextValue, { maxWidth: 530, maxHeight: 438, fontFamily: 'Gothic', minFontSize: 45, maxFontSize: 120 });

            if (variation.leftBrakes) {
              leftTextP.innerText = leftTextValue;
            } else {
              leftTextP.innerHTML = leftAlg.lines.join('<br/>');
            }

            if (variation.leftFontSize) {
              leftText.style.fontSize = `${variation.leftFontSize}px`;
            } else {
              leftText.style.fontSize = `${leftAlg.fontSize}px`;
            }

            leftText.appendChild(leftTextP);

            leftTextHolder.appendChild(leftAuthorRow);
            leftTextHolder.appendChild(leftText);

            const rightTextHolder = document.createElement('div');
            rightTextHolder.classList.add('right-text-holder-var32');
            const middleBreak = document.createElement('div');
            middleBreak.classList.add('break-var32');
            imageHolder.appendChild(middleBreak);
            const authorRow = document.createElement('div');
            authorRow.classList.add('author-row-var32');

            const authorImage = new Image();
            authorImage.classList.add('author-image-var32');

            const authorName = document.createElement('p');
            authorName.innerText = variation.authorName;
            authorName.classList.add('author-name-var32');

            if (variation.channelNameColor === 'blue') {
              authorName.classList.add('author-name-var32__blue');
            }

            if (variation.channelImageOption === 'reddit') {
              authorImage.src = RedditLogo;
              authorRow.appendChild(authorImage);
            } else {
              if (!!variation.authorImage) {
                authorImage.src = variation.authorImage;
                authorRow.appendChild(authorImage);
              }
            }

            authorRow.appendChild(authorName);

            const rightText = document.createElement('div');
            rightText.classList.add('right-text-var32');

            const rightTextP = document.createElement('p');

            let text = replaceQuotes(variation.rightText);

            const algorithmResult = getOptimalFontSize(text, { maxWidth: 530, maxHeight: 438, fontFamily: 'Gothic', minFontSize: 45, maxFontSize: 120 });

            if (variation.brakes) {
              rightTextP.innerText = text;
            } else {
              rightTextP.innerHTML = algorithmResult.lines.join('<br/>');
            }

            if (variation.fontSize) {
              rightText.style.fontSize = `${variation.fontSize}px`;
            } else {
              rightText.style.fontSize = `${algorithmResult.fontSize}px`;
            }

            rightText.appendChild(rightTextP);

            rightTextHolder.appendChild(authorRow);

            rightTextHolder.appendChild(rightText);

            imageHolder.appendChild(leftTextHolder);
            imageHolder.appendChild(rightTextHolder);

            // Temporarily append to DOM to get accurate measurements
            document.body.appendChild(imageHolder);

            const rightTextWidth = rightTextP.offsetWidth;
            const leftTextWidth = leftTextP.offsetWidth;

            const paddingLeftRight = (534 - rightTextWidth) / 2;
            const paddingLeftLeft = (534 - leftTextWidth) / 2;

            authorRow.style.paddingLeft =
              paddingLeftRight < 90 ? `${paddingLeftRight}px` : '90px';

            leftAuthorRow.style.paddingLeft =
              paddingLeftLeft < 90 ? `${paddingLeftLeft}px` : '90px';

            // Remove from DOM before returning
            document.body.removeChild(imageHolder);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var32',
            pixelRatio: 1,
          }
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          }
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
